import {
    InputNode,
} from '../../documents/types';

export const isEmptyValue = (value: any): boolean =>
    value === undefined ||
    value === null ||
    value === '' ||
    value === '<p></p>' ||
    (typeof value === 'string' && value.replace(/<[^>]*>/g, '').trim() === '') ||
    (Array.isArray(value) && value.length === 0);

// format a date string in UTC to avoid day-shift in negative-offset timezones
export const formatDate = (value?: string): string => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

/**
 * evaluate a schema condition expression against document data
 * Supports: ||, &&, !, !==, ===, ==, >, <, and bare truthy key checks.
 */
export const evaluateCondition = (
    condition: string | undefined,
    data: Record<string, any>,
    localContext?: Record<string, any>
): boolean => {
    if (!condition) return false;

    const getValue = (key: string): any => {
        if (key.endsWith('.length')) {
            const base = getValue(key.slice(0, -7));
            return Array.isArray(base) ? base.length : 0;
        }
        if (localContext?.[key] !== undefined) return localContext[key];
        return data[key];
    };

    const evaluate = (expr: string): boolean => {
        const t = expr.trim();
        if (t.includes('||')) return t.split('||').some((p) => evaluate(p));
        if (t.includes('&&')) return t.split('&&').every((p) => evaluate(p));
        if (t.startsWith('!') && !t.slice(1).match(/[=<>]/))
            return !getValue(t.slice(1).trim());
        if (t.includes('!==')) {
            const [k, v] = t.split('!==').map((s) => s.trim());
            return getValue(k) !== v.replace(/['"]/g, '');
        }
        if (t.includes('===')) {
            const [k, v] = t.split('===').map((s) => s.trim());
            return getValue(k) === v.replace(/['"]/g, '');
        }
        if (t.includes('==')) {
            const [k, v] = t.split('==').map((s) => s.trim());
            // eslint-disable-next-line eqeqeq
            return getValue(k) == v.replace(/['"]/g, '');
        }
        if (t.includes('>')) {
            const [k, v] = t.split('>').map((s) => s.trim());
            return Number(getValue(k)) > Number(v);
        }
        if (t.includes('<')) {
            const [k, v] = t.split('<').map((s) => s.trim());
            return Number(getValue(k)) < Number(v);
        }
        return !!getValue(t);
    };

    return evaluate(condition);
};

export const resolveFixedOptionLabel = (
    field: InputNode,
    value: any
): string => {
    if (value === undefined || value === null) return '';
    const fixed = field.data?.options?.fixed;
    if (fixed) {
        const option = fixed.find((o) => o.value === value);
        if (option) return option.label;
    }
    return String(value);
};
